import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FooterComponent } from './footer/footer.component';
import { LucideAngularModule, icons, Edit, Edit2, Edit3, FileEdit, FolderEdit, CircleHelp } from 'lucide-angular';

const allIcons = { ...icons, Edit, Edit2, Edit3, FileEdit, FolderEdit, CircleHelp };
import {TranslateModule} from "@ngx-translate/core";
import {RouterModule} from "@angular/router";
import { TopbarComponent } from './topbar/topbar.component';
import { NgbDropdownModule, NgbTooltipModule } from "@ng-bootstrap/ng-bootstrap";
import {SimplebarAngularModule} from "simplebar-angular";
import { SidebarComponent } from './sidebar/sidebar.component';
import { MatIconButton } from "@angular/material/button";
import { PagetitleComponent } from './pagetitle/pagetitle.component';
import { MatIconModule } from '@angular/material/icon';
import { EmptyStateComponent } from './empty-state/empty-state.component';
import { StatCardComponent } from './stat-card/stat-card.component';
import { FormSectionComponent } from './form-section/form-section.component';
import { SkeletonComponent } from './skeleton/skeleton.component';
import { ButtonComponent } from './ui/button/button.component';
import { ToggleComponent } from './ui/toggle/toggle.component';
import { SelectComponent } from './ui/select/select.component';
import { TabsComponent } from './ui/tabs/tabs.component';
import { TabPanelComponent } from './ui/tabs/tab-panel.component';
import { AccordionComponent } from './ui/accordion/accordion.component';
import { AccordionPanelComponent } from './ui/accordion/accordion-panel.component';
import { SpinnerComponent } from './ui/spinner/spinner.component';
import { SortableHeaderDirective } from './ui/sortable-header/sortable-header.directive';
import { AvatarInitialComponent } from './ui/avatar-initial/avatar-initial.component';
import { CardComponent } from './ui/card/card.component';
import { AlertContainerComponent } from './ui/alert/alert.component';
import { ModalComponent } from './ui/modal/modal.component';
import { ConfirmationDialogComponent } from './ui/confirmation-dialog/confirmation-dialog.component';
import { PaginationComponent } from './ui/pagination/pagination.component';
import { DropdownComponent } from './ui/dropdown/dropdown.component';
import { ClickOutsideDirective } from './ui/dropdown/click-outside.directive';
import { CounterComponent } from './ui/counter/counter.component';
import { DataTableComponent } from './ui/data-table/data-table.component';
import { LoadingStateComponent } from './ui/loading-state/loading-state.component';
import { ImpersonationBannerComponent } from './impersonation-banner/impersonation-banner.component';
import { NotificationBellComponent } from './notification-bell/notification-bell.component';
import { NotificationCenterComponent } from './notification-center/notification-center.component';
import { ConciergeChatComponent } from './concierge-chat/concierge-chat.component';
import { PasswordHintsComponent } from './ui/password-hints/password-hints.component';
import { BadgeComponent } from './ui/badge/badge.component';
import { SegmentedComponent } from './ui/segmented/segmented.component';
import { SegmentedItemComponent } from './ui/segmented/segmented-item.component';
import { PageHeadingComponent } from './ui/page-heading/page-heading.component';
import { SectionHeadingComponent } from './ui/section-heading/section-heading.component';
import { HcFormFieldComponent } from './ui/input/form-field/form-field.component';
import { HcInputComponent } from './ui/input/input/input.component';
import { InputSearchComponent } from './ui/input/input-search/input-search.component';
import { InputPhoneComponent } from './ui/input/input-phone/input-phone.component';
import { InputDropdownComponent } from './ui/input/input-dropdown/input-dropdown.component';
import { InputCountrySelectComponent } from './ui/input/input-country-select/input-country-select.component';
import { InputRangeComponent } from './ui/input/input-range/input-range.component';
import { HcTextareaComponent } from './ui/input/textarea/textarea.component';
import { HcCheckboxComponent } from './ui/input/checkbox/checkbox.component';
import { HcRadioGroupComponent } from './ui/input/radio/radio.component';
import { HcChipComponent } from './ui/chip/chip.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { ErrorBannerComponent } from './ui/error-banner/error-banner.component';

@NgModule({
  declarations: [
    FooterComponent,
    TopbarComponent,
    SidebarComponent,
    PagetitleComponent,
    EmptyStateComponent,
    StatCardComponent,
    FormSectionComponent,
    SkeletonComponent,
    ButtonComponent,
    ToggleComponent,
    SelectComponent,
    TabsComponent,
    TabPanelComponent,
    AccordionComponent,
    AccordionPanelComponent,
    SpinnerComponent,
    SortableHeaderDirective,
    AvatarInitialComponent,
    CardComponent,
    AlertContainerComponent,
    ModalComponent,
    ConfirmationDialogComponent,
    PaginationComponent,
    DropdownComponent,
    ClickOutsideDirective,
    CounterComponent,
    DataTableComponent,
    LoadingStateComponent,
    ImpersonationBannerComponent,
    NotificationBellComponent,
    NotificationCenterComponent,
    ConciergeChatComponent,
    PasswordHintsComponent,
    BadgeComponent,
    SegmentedComponent,
    SegmentedItemComponent,
    PageHeadingComponent,
    SectionHeadingComponent,
    HcFormFieldComponent,
    HcInputComponent,
    InputSearchComponent,
    InputPhoneComponent,
    InputDropdownComponent,
    InputCountrySelectComponent,
    InputRangeComponent,
    HcTextareaComponent,
    HcCheckboxComponent,
    HcRadioGroupComponent,
    HcChipComponent,
    ErrorBannerComponent
  ],
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule.pick(allIcons),
    TranslateModule.forChild(),
    RouterModule,
    NgbDropdownModule,
    NgbTooltipModule,
    SimplebarAngularModule,
    LucideAngularModule,
    MatIconButton,
    MatIconModule,
    NgSelectModule
  ],
  exports: [
    FooterComponent,
    TopbarComponent,
    SidebarComponent,
    PagetitleComponent,
    EmptyStateComponent,
    StatCardComponent,
    FormSectionComponent,
    SkeletonComponent,
    ButtonComponent,
    ToggleComponent,
    SelectComponent,
    TabsComponent,
    TabPanelComponent,
    AccordionComponent,
    AccordionPanelComponent,
    SpinnerComponent,
    SortableHeaderDirective,
    AvatarInitialComponent,
    CardComponent,
    AlertContainerComponent,
    ModalComponent,
    ConfirmationDialogComponent,
    PaginationComponent,
    DropdownComponent,
    ClickOutsideDirective,
    CounterComponent,
    DataTableComponent,
    LoadingStateComponent,
    ImpersonationBannerComponent,
    NotificationBellComponent,
    NotificationCenterComponent,
    ConciergeChatComponent,
    PasswordHintsComponent,
    BadgeComponent,
    SegmentedComponent,
    SegmentedItemComponent,
    PageHeadingComponent,
    SectionHeadingComponent,
    HcFormFieldComponent,
    HcInputComponent,
    InputSearchComponent,
    InputPhoneComponent,
    InputDropdownComponent,
    InputCountrySelectComponent,
    InputRangeComponent,
    HcTextareaComponent,
    HcCheckboxComponent,
    HcRadioGroupComponent,
    HcChipComponent,
    ErrorBannerComponent,
    LucideAngularModule
  ]
})
export class ComponentsModule { }
